class M90CartRecommendations extends HTMLElement {
  static get observedAttributes() {
    return [
      'data-product-ids',
      'data-cart-product-ids',
      'data-cart-signature'
    ];
  }

  connectedCallback() {
    this.loadRecommendations();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected) return;
    if (oldValue === newValue) return;

    this.loadRecommendations();
  }

  getIdList(value) {
    return [...new Set(
      String(value || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    )];
  }

  async fetchRecommendationsForProduct(productId, sectionId, limit) {
    const root =
      window.Shopify?.routes?.root || '/';

    const url =
      `${root}recommendations/products` +
      `?product_id=${encodeURIComponent(productId)}` +
      `&limit=${encodeURIComponent(limit)}` +
      `&section_id=${encodeURIComponent(sectionId)}` +
      `&intent=related`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(
        `M90 recommendations request failed: ${response.status}`
      );
    }

    const htmlText = await response.text();
    const template = document.createElement('template');

    template.innerHTML = htmlText;

    const content =
      template.content.querySelector(
        '.m90-cart-recommendations__content'
      );

    const products = content
      ? [...content.querySelectorAll(
          '[data-m90-recommendation-product]'
        )]
      : [];

    return {
      htmlText,
      template,
      content,
      products
    };
  }

  buildBalancedProductList(responses, cartProductIds, limit) {
    const selected = [];
    const selectedIds = new Set();

    const maxDepth = responses.reduce(
      (max, response) =>
        Math.max(max, response.products.length),
      0
    );

    for (
      let depth = 0;
      depth < maxDepth && selected.length < limit;
      depth += 1
    ) {
      for (const response of responses) {
        if (selected.length >= limit) break;

        const product = response.products[depth];
        if (!product) continue;

        const productId =
          product.dataset.productId?.trim() || '';

        if (!productId) continue;
        if (cartProductIds.has(productId)) continue;
        if (selectedIds.has(productId)) continue;

        selectedIds.add(productId);
        selected.push(product.cloneNode(true));
      }
    }

    return selected;
  }

  async loadRecommendations() {
    const productIds =
      this.getIdList(this.dataset.productIds);

    const cartProductIds =
      new Set(
        this.getIdList(this.dataset.cartProductIds)
      );

    const sectionId =
      this.dataset.sectionId || 'm90-cart-recommendations';

    const limit =
      Math.max(
        1,
        Number.parseInt(this.dataset.limit || '4', 10) || 4
      );

    if (!productIds.length) {
      this.replaceChildren();
      this.hidden = true;
      return;
    }

    const sourceProductIds =
      productIds.slice(0, 4);

    const requestKey = [
      sourceProductIds.join(','),
      [...cartProductIds].join(','),
      this.dataset.cartSignature || '',
      sectionId,
      limit
    ].join('|');

    if (this.dataset.loadedKey === requestKey) return;
    if (this.dataset.loadingKey === requestKey) return;

    this.dataset.loadingKey = requestKey;

    try {
      const settled =
        await Promise.allSettled(
          sourceProductIds.map((productId) =>
            this.fetchRecommendationsForProduct(
              productId,
              sectionId,
              limit
            )
          )
        );

      const responses =
        settled
          .filter(
            (result) =>
              result.status === 'fulfilled' &&
              result.value.content &&
              result.value.products.length
          )
          .map((result) => result.value);

      if (!responses.length) {
        this.replaceChildren();
        this.hidden = true;
        return;
      }

      const selectedProducts =
        this.buildBalancedProductList(
          responses,
          cartProductIds,
          limit
        );

      if (!selectedProducts.length) {
        this.replaceChildren();
        this.hidden = true;
        return;
      }

      const outputTemplate =
        responses[0].template;

      const productsContainer =
        outputTemplate.content.querySelector(
          '.m90-cart-recommendations__products'
        );

      if (!productsContainer) {
        this.replaceChildren();
        this.hidden = true;
        return;
      }

      productsContainer.replaceChildren(
        ...selectedProducts
      );

      this.innerHTML =
        outputTemplate.innerHTML;

      this.hidden = false;
      this.dataset.loadedKey = requestKey;
    } catch (error) {
      console.warn(
        '[M90] No se pudieron cargar las recomendaciones.',
        error
      );

      this.replaceChildren();
      this.hidden = true;
    } finally {
      if (this.dataset.loadingKey === requestKey) {
        delete this.dataset.loadingKey;
      }
    }
  }
}

if (
  !customElements.get('m90-cart-recommendations')
) {
  customElements.define(
    'm90-cart-recommendations',
    M90CartRecommendations
  );
}